import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMobileConversations } from '@/hooks/messaging/mobile/useMobileConversations';
import { useUnreadSummary } from '@/hooks/messaging/useUnreadSummary';
import { isNativeMobile } from '@/lib/mobile-mode';
import { clearAppIconBadge } from '@/lib/push-notifications';
import {
  initialBadgeSyncState,
  nextBadgeSync,
  type BadgeSyncState,
} from '@/lib/app-icon-badge';
import { NATIVE_MESSAGE_ROLES } from '../../../../supabase/functions/_shared/unread-badge';

// Keeps the iOS/Android app-icon badge in step with the inbox.
//
// The number itself comes down with the push (send-push writes an absolute
// per-recipient badge). This component owns the other half: zeroing the icon
// once the user has actually read everything, which no server can observe.
// See src/lib/app-icon-badge.ts for why "clear at zero" is the only move
// available without a native badge plugin.
//
// Renders nothing, and mounts nothing at all on web.

const useClearBadgeWhenRead = (total: number | null | undefined) => {
  const stateRef = useRef<BadgeSyncState>(initialBadgeSyncState);

  useEffect(() => {
    const { state, clear } = nextBadgeSync(stateRef.current, total);
    stateRef.current = state;
    if (clear) void clearAppIconBadge();
  }, [total]);
};

/**
 * admin / super_admin get the whole messenger on the phone, so their badge
 * tracks every thread — the same total the web unread bell reads.
 */
const FullInboxBadgeSync = () => {
  const { totalUnreadCount, isPending, isError } = useUnreadSummary();
  useClearBadgeWhenRead(isPending || isError ? null : totalUnreadCount);
  return null;
};

/**
 * Every other role is scheduling-only on native: the one inbox they can open is
 * /schedules/messages, so that is the only count their icon should reflect.
 */
const SchedulingInboxBadgeSync = () => {
  const { totalUnreadCount, isPending, isError } = useMobileConversations();
  useClearBadgeWhenRead(isPending || isError ? null : totalUnreadCount);
  return null;
};

export const AppIconBadgeSync = () => {
  const { profile } = useAuth();
  // Web has no app icon to badge, and an unauthenticated shell has no inbox to
  // count. Bail before either hook mounts so we don't add a poll for nothing.
  if (!isNativeMobile() || !profile) return null;
  return NATIVE_MESSAGE_ROLES.includes(profile.role) ? (
    <FullInboxBadgeSync />
  ) : (
    <SchedulingInboxBadgeSync />
  );
};
